import { AiState } from './ai';
import { Input, newCar } from './car';
import { datan2, hashNumbers } from './fmath';
import { Racer, RaceEvent, RaceWorld, RacerSpec } from './race';
import type { Track } from './track';
import { TICK_HZ } from './tuning';

/**
 * Head-to-Head (design.md A6, B8.1): 2-4 cars on one shared screen.
 *
 * The camera follows the race leader. A car that stays outside the view box
 * for a quarter of a second is dropped from the round and loses a light.
 * When one car is left on screen the round ends: the winner gains one light
 * (with two players that's the original's tug of war: what you lose, they
 * gain), and everyone still in the match restarts in formation where the
 * leader was. Lose all your lights and you're out; last one standing wins.
 * Gaining one light per round, not one per drop, means 3-4 player matches
 * always shrink towards an end.
 *
 * The view box is a fixed size in world pixels (not the player's window),
 * so a match plays the same on every screen and replays exactly.
 */

/** The shared view, in world pixels. The renderer fits this box to the screen. */
export const COUCH_VIEW_W = 576;
export const COUCH_VIEW_H = 324;
/** How long a car may be off screen before it's dropped. */
export const OFF_SCREEN_TICKS = Math.round(0.25 * TICK_HZ);
const COUNTDOWN_TICKS = Math.round(1.5 * TICK_HZ);
const ROUND_OVER_TICKS = Math.round(2.2 * TICK_HZ);
/** A round that drags on: the box starts to shrink (sudden death). */
export const SUDDEN_DEATH_TICKS = 30 * TICK_HZ;
const SHRINK_TICKS = 20 * TICK_HZ;
const MIN_BOX = 0.4;
/** Camera easing per tick (≈0.1 s time constant at 120 Hz). */
const CAM_EASE = 0.08;
/** Formation: rows 30 px apart, staggered, cars 12 px either side of the line. */
const ROW_GAP = 30;
const STAGGER = 14;
const COL_OFFSET = 12;
const FORMATION_AHEAD = 22;

export type CouchPhase = 'countdown' | 'racing' | 'roundOver' | 'over';

export type CouchEvent =
  | RaceEvent
  | { type: 'roundStart'; round: number }
  | { type: 'go' }
  | { type: 'offScreen'; racer: number }
  | { type: 'dropped'; racer: number; lightsLeft: number }
  | { type: 'suddenDeath' }
  | { type: 'roundOver'; winner: number; gained: number }
  | { type: 'matchOver'; winner: number };

export interface CouchOptions {
  lights: number;
  aiPace: number;
  seed: number;
}

export class CouchMatch {
  readonly race: RaceWorld;
  readonly opts: CouchOptions;
  readonly lights: number[];
  readonly roundWins: number[];
  readonly drops: number[];
  /** Still on screen in the current round. */
  readonly inRound: boolean[];
  readonly offTicks: number[];
  phase: CouchPhase = 'countdown';
  phaseTicks = 0;
  round = 1;
  /** Racing ticks in this round (drives sudden death). */
  roundTicks = 0;
  /** View box size multiplier: 1, shrinking toward MIN_BOX in sudden death. */
  boxScale = 1;
  pending = 0;
  winner = -1;
  camX = 0;
  camY = 0;
  tick = 0;
  events: CouchEvent[] = [];

  constructor(track: Track, specs: RacerSpec[], opts: CouchOptions) {
    this.opts = opts;
    const grid = specs.map((_, i) => i);
    // Laps never end a Head-to-Head; respawn keeps crashed cars in play
    // (although they'll usually be dropped before they rejoin).
    this.race = new RaceWorld(track, specs, grid, { laps: 1e9, crashRule: 'respawn', aiPace: opts.aiPace, seed: opts.seed });
    const n = specs.length;
    this.lights = new Array(n).fill(opts.lights);
    this.roundWins = new Array(n).fill(0);
    this.drops = new Array(n).fill(0);
    this.inRound = new Array(n).fill(true);
    this.offTicks = new Array(n).fill(0);
    const c = this.centroid();
    this.camX = c[0];
    this.camY = c[1];
    this.events.push({ type: 'roundStart', round: 1 });
  }

  get track(): Track {
    return this.race.track;
  }

  get racers(): Racer[] {
    return this.race.racers;
  }

  /** Leader of the current round (by race distance), among cars still in it. */
  leader(): number {
    let best = -1;
    let bp = -Infinity;
    for (const r of this.race.racers) {
      if (!this.inRound[r.index]) continue;
      const p = this.race.progress(r);
      if (p > bp) {
        bp = p;
        best = r.index;
      }
    }
    return best;
  }

  /** Half-extents of the (possibly shrunk) view box. */
  halfBox(): [number, number] {
    return [(COUCH_VIEW_W / 2) * this.boxScale, (COUCH_VIEW_H / 2) * this.boxScale];
  }

  /** One fixed tick. `human` maps racer index to input (cars not in it are AI). */
  step(human: ReadonlyMap<number, Input> = new Map()): void {
    this.events = [];
    this.tick++;
    this.phaseTicks++;
    switch (this.phase) {
      case 'countdown':
        if (this.phaseTicks >= COUNTDOWN_TICKS) this.setPhase('racing');
        return;
      case 'roundOver':
        if (this.phaseTicks >= ROUND_OVER_TICKS) this.nextRound();
        return;
      case 'over':
        return;
      case 'racing':
        break;
    }

    const inputs = new Map<number, Input>();
    for (const [i, inp] of human) if (this.inRound[i]) inputs.set(i, inp);
    this.race.step(inputs);
    for (const e of this.race.events) this.events.push(e);

    this.roundTicks++;
    if (this.roundTicks === SUDDEN_DEATH_TICKS) this.events.push({ type: 'suddenDeath' });
    if (this.roundTicks > SUDDEN_DEATH_TICKS) {
      const t = Math.min(1, (this.roundTicks - SUDDEN_DEATH_TICKS) / SHRINK_TICKS);
      this.boxScale = 1 - (1 - MIN_BOX) * t;
    }

    // Camera: ease toward the leader.
    const lead = this.leader();
    const lc = this.race.racers[lead].car;
    this.camX += (lc.x - this.camX) * CAM_EASE;
    this.camY += (lc.y - this.camY) * CAM_EASE;

    // Who has fallen off the screen?
    const [hw, hh] = this.halfBox();
    for (const r of this.race.racers) {
      const i = r.index;
      if (!this.inRound[i] || i === lead) {
        this.offTicks[i] = 0;
        continue;
      }
      const dx = r.car.x - this.camX;
      const dy = r.car.y - this.camY;
      const off = dx > hw || dx < -hw || dy > hh || dy < -hh;
      if (!off) {
        this.offTicks[i] = 0;
        continue;
      }
      if (this.offTicks[i] === 0) this.events.push({ type: 'offScreen', racer: i });
      if (++this.offTicks[i] > OFF_SCREEN_TICKS) this.drop(r);
    }

    if (this.inRoundCount() <= 1) this.endRound(lead);
  }

  private drop(r: Racer): void {
    const i = r.index;
    this.inRound[i] = false;
    this.offTicks[i] = 0;
    r.outProgress = this.race.progress(r);
    r.status = 'out';
    this.lights[i]--;
    this.drops[i]++;
    this.pending++;
    this.events.push({ type: 'dropped', racer: i, lightsLeft: this.lights[i] });
  }

  private inRoundCount(): number {
    let n = 0;
    for (const v of this.inRound) if (v) n++;
    return n;
  }

  private endRound(winner: number): void {
    const gained = this.pending > 0 ? 1 : 0;
    this.lights[winner] += gained;
    this.roundWins[winner]++;
    this.events.push({ type: 'roundOver', winner, gained });
    this.pending = 0;
    this.winner = winner;
    this.setPhase('roundOver');
  }

  private nextRound(): void {
    const alive = this.lights.map((l, i) => (l > 0 ? i : -1)).filter((i) => i >= 0);
    if (alive.length <= 1) {
      this.winner = alive[0] ?? this.winner;
      this.setPhase('over');
      this.events.push({ type: 'matchOver', winner: this.winner });
      return;
    }
    // Restart where the round's winner was, everyone in race order behind it.
    const order = [this.winner, ...this.race.standings().filter((i) => i !== this.winner)].filter((i) => this.lights[i] > 0);
    this.formation(order);
    for (const r of this.race.racers) {
      this.inRound[r.index] = this.lights[r.index] > 0;
      this.offTicks[r.index] = 0;
      if (this.lights[r.index] <= 0) {
        r.status = 'out';
      }
    }
    this.round++;
    this.roundTicks = 0;
    this.boxScale = 1;
    const lc = this.race.racers[order[0]].car;
    const c = this.centroid();
    this.camX = (lc.x + c[0]) / 2;
    this.camY = (lc.y + c[1]) / 2;
    this.events.push({ type: 'roundStart', round: this.round });
    this.setPhase('countdown');
  }

  /** Line the cars up two-wide just past the leader's last gate, leader in front. */
  private formation(order: number[]): void {
    const race = this.race;
    const track = race.track;
    const lead = race.racers[order[0]];
    const lap = lead.lap;
    const path = track.routes[0];
    const g = track.gates[lap.lastGate];
    const s0 = lap.startTick < 0 ? path.project(track.gates[0].cx, track.gates[0].cy).s : path.project(g.cx, g.cy).s;
    const rows = Math.floor((order.length - 1) / 2);
    order.forEach((idx, slot) => {
      const r = race.racers[idx];
      const row = Math.floor(slot / 2);
      const lat = slot % 2 === 0 ? -COL_OFFSET : COL_OFFSET;
      const p = path.pointAt(s0 + FORMATION_AHEAD + (rows - row) * ROW_GAP - (slot % 2) * STAGGER);
      const car = newCar(p.x - p.uy * lat, p.y + p.ux * lat, datan2(p.uy, p.ux));
      car.bonusMph = r.car.bonusMph;
      r.car = car;
      r.lap = { ...lap, bestLapTicks: r.lap.bestLapTicks, lastLapTicks: r.lap.lastLapTicks, streakDist: 0, valid: true };
      r.status = 'running';
      r.ghostTicks = 0;
      r.lowSpeedTicks = 0;
      resetAi(r.ai, r.lap.lapsDone);
    });
  }

  private centroid(): [number, number] {
    let x = 0;
    let y = 0;
    let n = 0;
    for (const r of this.race.racers) {
      if (!this.inRound[r.index]) continue;
      x += r.car.x;
      y += r.car.y;
      n++;
    }
    return n ? [x / n, y / n] : [0, 0];
  }

  private setPhase(p: CouchPhase): void {
    this.phase = p;
    this.phaseTicks = 0;
    if (p === 'racing') this.events.push({ type: 'go' });
  }

  /** Countdown number to show (3, 2, 1), or 0 when not counting down. */
  countdown(): number {
    if (this.phase !== 'countdown') return 0;
    return 3 - Math.floor((this.phaseTicks * 3) / COUNTDOWN_TICKS);
  }

  /** Run without inputs (all-AI matches, tests). */
  simulate(maxTicks: number): void {
    for (let i = 0; i < maxTicks && this.phase !== 'over'; i++) this.step();
  }

  hash(): number {
    return hashNumbers([this.race.hash(), this.tick, this.round, this.camX, this.camY, this.boxScale, ...this.lights, ...this.offTicks]);
  }
}

function resetAi(ai: AiState, lapsDone: number): void {
  ai.route = 0;
  ai.lastLap = lapsDone;
  ai.lastSeg = -1;
  ai.lane = 0;
  ai.laneTarget = 0;
  ai.overcook = 1;
  ai.passTarget = -1;
  ai.passTicks = 0;
  ai.passCooldown = 60;
  ai.fluffTicks = 0;
  ai.stuckTicks = 0;
  ai.reverseTicks = 0;
}
